/**
 * Inspired by Docusaurus @theme-ui/tabs
 * @see https://github.com/facebook/docusaurus/blob/main/packages/docusaurus-theme-classic/src/theme/Tabs/index.tsx
 */
import { Children, JSX, ReactElement, isValidElement } from 'react'

import {
  Tab,
  TabContent,
  TabsList,
  Tabs as TabsRoot,
} from 'components/atomic/atoms/tabs'

const TabItem = TabContent

type TabItemProps = {
  label: string
  children?: ReactElement
}

const isTabItem = (
  child: ReactElement<object>,
): child is ReactElement<TabItemProps> => {
  return 'label' in child.props
}

const Tabs = ({ children }: { children: JSX.Element[] }) => {
  const items = Children.map(children, child => {
    if (isValidElement<TabItemProps>(child) && isTabItem(child)) {
      return child
    }
  })

  if (!items) return null

  return (
    <TabsRoot
      data-widget="tabs"
      defaultValue={items[0].props.label}
      css={{
        border: '1px solid $bg3',
        borderRadius: '$md',
      }}
    >
      <TabsList
        css={{
          bgc: '$bg2',
          px: '$4',
          borderTopLeftRadius: '$md',
          overflowX: 'auto',
        }}
      >
        {items.map(({ props: { label } }) => (
          <Tab key={label} value={label} css={{ whiteSpace: 'nowrap' }}>
            {label}
          </Tab>
        ))}
      </TabsList>

      {items.map(({ props: { label, children } }, i) => (
        <TabContent
          key={i}
          value={label}
          css={{
            '& pre': {
              m: 0,
              border: 'unset',
              borderRadiusTop: 'unset',
            },
            '& p': {
              px: '$4',
            },
          }}
        >
          {children}
        </TabContent>
      ))}
    </TabsRoot>
  )
}

export { Tabs, TabItem }
